import { CheckOutlined, CloseOutlined, FolderOpenOutlined, MoreOutlined, SearchOutlined } from '@ant-design/icons';
import { Input, Tooltip } from 'antd';
import React, { FC, useState } from 'react';
import styled from 'styled-components';
import { api } from '../../api/api';
import { Coach, User } from '../../types/userTypes';
import colors from '../../utils/colors';
import { debounce } from '../../utils/common';
import { CREATED_AT, DELETED, EMAIL, FIRST_NAME, ID, SEARCH_BY_EMAIL } from '../../utils/constants/labels';
import { DatePreview, getSubdomain } from '../../utils/helpers';
import { environment } from '../../config/configConstants';
import { DATE_FORMAT, formatDate } from '../../utils/timeUtils';
import PageHeader from '../common/layout/PageHeader';
import UserActionsModal from '../common/modal/UserActionsModal';
import Table from '../common/table/Table';
import CoachMembershipsModal from '../common/modal/CoachMembershipsModal';

const columns = (openCoachMemberships: (coach: Coach) => void, openUserActionsModal: (user: User) => void) => [
  {
    title: ID,
    dataIndex: 'id',
    sorter: true,
    width: '5%',
    defaultSortOrder: 'descend'
  },
  {
    title: FIRST_NAME,
    dataIndex: 'firstName',
    width: '15%'
  },
  {
    title: EMAIL,
    dataIndex: 'email',
    sorter: true,
    width: '50%'
  },
  {
    title: 'Memberships',
    dataIndex: 'moreOptions',
    width: '10%',
    render: (value, row) =>
      !row.deleted && (
        <Tooltip title={'Open'}>
          <FolderOpenOutlined onClick={() => openCoachMemberships(row)} />
        </Tooltip>
      )
  },
  {
    title: CREATED_AT,
    dataIndex: 'createdAt',
    sorter: true,
    width: '10%',
    render: value => (value ? <DatePreview>{formatDate(value, DATE_FORMAT)}</DatePreview> : null)
  },
  {
    title: DELETED,
    dataIndex: 'deleted',
    width: '5%',
    render: value => (value ? <CheckOutlined /> : <CloseOutlined />)
  },
  {
    title: '',
    dataIndex: 'moreOptions',
    width: '5%',
    render: (value, row) =>
      !row.deleted && (
        <Tooltip title={'More options'}>
          <MoreOutlined onClick={() => openUserActionsModal(row)} />
        </Tooltip>
      )
  }
];

type Props = {};

const Coaches: FC<Props> = ({}) => {
  const [refreshTable, setRefreshTable] = useState<Function>();

  const [email, setEmail] = useState('');

  const setEmailDebounced = debounce(value => setEmail(value), 400);
  const [editUser, setEditUser] = useState<User>();
  const [openedCoach, setOpenedCoach] = useState<Coach>();

  return (
    <Container>
      <PageHeader
        title={'Coaches'}
        extra={[
          <Input
            key={1}
            onChange={e => setEmailDebounced(e.target.value)}
            placeholder={SEARCH_BY_EMAIL}
            prefix={<SearchOutlined />}
          />
        ]}
      />
      <Table
        columns={columns(
          (coach: Coach) => setOpenedCoach(coach),
          (user: User) => setEditUser(user)
        )}
        getData={api.getAllCoaches}
        idPropName={'id'}
        searchPropName={'email'}
        searchPropValue={email}
        bindFetchFn={setRefreshTable}
      />
      <UserActionsModal
        user={editUser ? { ...editUser, role: 'COACH' } : undefined}
        websiteSubdomain={getSubdomain(environment)}
        onClose={() => setEditUser(undefined)}
        onUserUpdate={refreshTable!}
      />
      <CoachMembershipsModal coach={openedCoach} onClose={() => setOpenedCoach(undefined)} />
    </Container>
  );
};

export default Coaches;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  flex: 1;
  background-color: ${colors.white};
  position: relative;
`;
